import { ApolloError } from '@apollo/client';

import {
  CartPaymentCardType,
  type IAddOrbitalAccountInput,
  ICommitDeliveryInput,
  ICommitOrderInput as ICommitOrderInputGenerated,
  IMutationCommitOrderArgs,
  IOrder,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import {
  CommitOrderDocument,
  GetOrderDocument,
  GetUserOrdersDocument,
} from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { client as legacyClient } from '@fhs-legacy/frontend/src/state/graphql/client';
import { Network } from '@fhs-legacy/frontend/src/utils/network/status';

import type {
  ApplePayContext,
  CommittedOrder,
  GiftCardContext,
  GooglePayContext,
  NewCardContext,
  OrbitalEncryptionResult,
  PipelineStepContext,
  PipelineStepResult,
  SavedCardContext,
  VaultResult,
  WorldpayEncryptionResult,
} from '..';
import {
  ApplePayDetails,
  GooglePayDetails,
  PaymentProcessor,
  PipelineBaseStep,
  ServiceMode,
  usePaymentFlowStore,
} from '..';
import { usePaymentMethodStore } from '../../state/payment-stores/payment-method-store';
import { VaultService } from '../services';
import {
  RECOVERY_WINDOW_MS,
  recoverSubmittedOrder,
} from '../services/recover-submitted-order';

function parseGraphQLErrorCode(error: unknown): string | null {
  if (error instanceof ApolloError && error.graphQLErrors?.length > 0) {
    const firstError = error.graphQLErrors[0];
    return (firstError.extensions?.code as string) || null;
  }
  return null;
}

function isNetworkCommitError(error: unknown): boolean {
  if (error instanceof ApolloError && error.networkError) {
    return true;
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      message.includes('network request failed') ||
      message.includes('load failed')
    );
  }

  return false;
}

/** Commit failed but the server returned a definitive rejection (e.g. payment declined). */
function isDefinitiveCommitFailure(error: unknown): boolean {
  if (isNetworkCommitError(error)) {
    return false;
  }

  if (error instanceof ApolloError && error.graphQLErrors?.length > 0) {
    return true;
  }

  return false;
}

interface ICommitOrderInput {
  rbiOrderId: string;
  fireOrderIn: number;
  creditType?: CartPaymentCardType;
  payment?: IPaymentInput;
  applePayDetails?: ApplePayDetails;
  googlePayDetails?: GooglePayDetails;
  pickUpTipsCents?: number;
  // storeEmail?: string;
  fullName?: string;
  fdAccountId?: string; // Used for gift card (prepaid) payments
}

interface IPaymentInput {
  fullName: string;
  applePayDetails?: ApplePayDetails;
  googlePayDetails?: GooglePayDetails;
  billingAddress?: {
    postalCode?: string;
  };
  ccMetadata?: {
    ccBin?: string;
    ccExpiryMonth?: string;
    ccExpiryYear?: string;
    ccLast4?: string;
  };
  orbitalInput?: {
    accountIdentifier?: string;
    encryptedPayload?: IAddOrbitalAccountInput;
    savePaymentMethod?: boolean;
  };
  worldpayInput?: {
    storedPaymentMethodId?: string;
    lowValueToken?: string;
    storePaymentMethod?: boolean;
  };
}

export class CommitOrderStep extends PipelineBaseStep {
  readonly name = 'CommitOrderStep';

  async execute(pipelineContext: PipelineStepContext): Promise<PipelineStepResult> {
    const pricedOrder = usePaymentFlowStore.getState().pricedOrder;
    const orderForAnalytics =
      pricedOrder?.rbiOrderId === pipelineContext.rbiOrderId ? pricedOrder : null;
    usePaymentFlowStore.getState().setCurrentState('committing');

    this.logger.log('Committing order', {
      rbiOrderId: pipelineContext.rbiOrderId,
      paymentMethod: pipelineContext.paymentMethod,
      processor: pipelineContext.processor,
    });

    // Step 1: Attempt to vault card if user chose "Save this card"
    // Make vaulting NON-BLOCKING: if vault fails, log warning but continue to commit order.
    // Backend will also attempt to vault via savePaymentMethod/storePaymentMethod flags.
    let vaultResult: VaultResult | null = null;
    if (this.shouldVaultCard(pipelineContext)) {
      // Reset failedVaulting flag at start of vault attempt
      usePaymentMethodStore.getState().setFailedVaulting(false);
      try {
        vaultResult = await this.attemptVaulting(pipelineContext as NewCardContext);
        if (!vaultResult.success || !vaultResult.accountIdentifier) {
          // Non-blocking: log warning but continue
          this.logger.log('Vault failed but continuing to commit order', {
            error: vaultResult?.error,
          });
        } else {
          // Vault succeeded - try to set as default payment method
          if (pipelineContext.onNewCardVaulted) {
            try {
              await pipelineContext.onNewCardVaulted(vaultResult.accountIdentifier);
              this.logger.log('Card vaulted and set as default successfully');
            } catch (defaultErr) {
              this.logger.logError(
                'Setting new card as default failed (card was still vaulted)',
                defaultErr
              );
            }
          }
        }
      } catch (err) {
        // Non-blocking: log error but continue to commit
        this.logger.logError('Vault request failed but continuing to commit order', err);
      }
    }

    // Step 2: Build commit order payload (use vaulted account for payment when vault succeeded)
    let commitOrderPayload: IMutationCommitOrderArgs;
    try {
      commitOrderPayload = this.buildCommitOrderPayload(pipelineContext, vaultResult);
    } catch (error) {
      if (error instanceof Error && error.message === 'RETRY_FROM_ENCRYPTION') {
        this.logger.log('Missing encryption result - pipeline should retry from EncryptionStep');
        return this.error(
          'MISSING_ENCRYPTION_RESULT',
          'Encryption result not available',
          'Payment processing error. Please try again. Error code: (Missing Encryption Result)',
          'EncryptionStep' // retryFromStep
        );
      }

      // Other build errors
      return this.error(
        'PAYLOAD_BUILD_FAILED',
        error instanceof Error ? error.message : 'Failed to build commit order payload',
        'Unable to process payment. Please try again. Error code: (Commit Payload Build)'
      );
    }

    // Step 3: Commit the order
    try {
      this.logger.log('Calling commitOrder mutation', {
        rbiOrderId: pipelineContext.rbiOrderId,
        hasDelivery: !!commitOrderPayload.delivery,
        skipCoolingPeriod: commitOrderPayload.skipCoolingPeriod,
        paymentStructure: {
          hasPayment: !!commitOrderPayload.input.payment,
          hasApplePayDetailsInPayment: !!commitOrderPayload.input.payment?.applePayDetails,
          hasGooglePayDetailsInPayment: !!commitOrderPayload.input.payment?.googlePayDetails,
          creditType: commitOrderPayload.input.creditType,
        },
      });

      const networkWatch = this.watchOfflineDuringCommit(RECOVERY_WINDOW_MS);

      let result;
      try {
        // Do not abort on offline — the request may already have reached the server.
        // Leave the mutation running and race reconnect/recover instead.
        const commitSettled = legacyClient
          .mutate<IOrder, IMutationCommitOrderArgs>({
            mutation: CommitOrderDocument,
            variables: commitOrderPayload,
            awaitRefetchQueries: true,
            refetchQueries: [
              { query: GetOrderDocument, variables: { rbiOrderId: pipelineContext.rbiOrderId } },
              { query: GetUserOrdersDocument },
            ],
          })
          .then(
            commitResult => ({ type: 'committed' as const, commitResult }),
            error => ({ type: 'commit-failed' as const, error })
          );

        const outcome = await Promise.race([
          commitSettled,
          networkWatch.offlineTimeout.then(() => ({ type: 'offline-timeout' as const })),
        ]);

        if (outcome.type === 'commit-failed') {
          throw outcome.error;
        }

        if (outcome.type === 'offline-timeout') {
          usePaymentFlowStore.getState().setCurrentState('confirming');

          const afterOffline = await Promise.race([
            commitSettled,
            recoverSubmittedOrder(pipelineContext.rbiOrderId).then(recovery => ({
              type: 'recovered' as const,
              recovery,
            })),
          ]);

          if (afterOffline.type === 'committed') {
            result = afterOffline.commitResult;
          } else if (afterOffline.type === 'commit-failed') {
            throw afterOffline.error;
          } else if (afterOffline.recovery.status === 'submitted') {
            this.logger.log('Offline recovery found order already submitted', {
              rbiOrderId: pipelineContext.rbiOrderId,
              orderStatus: afterOffline.recovery.order.status,
            });
            await this.fireCommitAnalytics(pipelineContext, orderForAnalytics);
            usePaymentFlowStore.getState().setCurrentState('insertRequested');
            return this.success();
          } else {
            const errorMessage =
              'Commit failed after recovery window with order still unsubmitted';
            this.logger.logError(errorMessage, {
              rbiOrderId: pipelineContext.rbiOrderId,
            });
            return this.error(
              'COMMIT_ORDER_FAILED',
              errorMessage,
              'Unable to complete your order. Please try again. Error code: (Order Failed)',
              'CommitOrderStep'
            );
          }
        } else {
          result = outcome.commitResult;
        }
      } finally {
        networkWatch.stop();
      }

      this.logger.log('Order committed successfully', {
        rbiOrderId: pipelineContext.rbiOrderId,
        // @ts-ignore - commitOrder status type varies by gateway
        status: result?.data?.commitOrder?.status,
      });

      await this.fireCommitAnalytics(pipelineContext, orderForAnalytics);
      usePaymentFlowStore.getState().setCurrentState('insertRequested');
      return this.success();
    } catch (error) {
      this.logger.logError('CommitOrder failed', error);

      const errorCode = parseGraphQLErrorCode(error);
      if (errorCode === 'COMMIT_ORDER_COOLING_PERIOD') {
        usePaymentFlowStore.getState().setCurrentState('error');
        this.logger.log(
          'Order rejected due to cooling period - duplicate order at same restaurant'
        );
        return this.error(
          'COMMIT_ORDER_COOLING_PERIOD',
          'Order rejected: cooling period active for this restaurant',
          'We see that you have recently placed an order at this restaurant. Please wait 5 minutes before placing another order at this restaurant.'
          // No retryFromStep - user must wait, not retry immediately
        );
      }

      if (errorCode === 'LOYALTY_TRANSACTION_VALIDATION_ERROR') {
        usePaymentFlowStore.getState().setCurrentState('error');
        this.logger.log('Order rejected due to loyalty transaction validation failure');
        // No retryFromStep - retrying with the same offer/reward will fail again;
        // user must remove the incentive or change the pickup time.
        return this.error(
          'COMMIT_ORDER_LOYALTY_VALIDATION',
          'Order rejected: loyalty transaction validation failed',
          "We couldn't validate the offer or reward applied to your order. Please review it and try again."
        );
      }

      const networkStatus = await Network.getStatus();
      // Definitive server rejections are only trustworthy while online. On 3G→offline
      // transitions the client may miss the response and must show confirming + recovery.
      if (isDefinitiveCommitFailure(error) && networkStatus.connected) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown commit order error';
        return this.error(
          'COMMIT_ORDER_FAILED',
          errorMessage,
          'Unable to complete your order. Please try again. Error code: (Order Failed)',
          'CommitOrderStep'
        );
      }

      const { isCommitRetry, setCurrentState, setIsOfflineDuringCommit } =
        usePaymentFlowStore.getState();
      if (!isCommitRetry) {
        setCurrentState('confirming');
        if (!networkStatus.connected) {
          setIsOfflineDuringCommit(true);
        }
      }
      const recovery = await recoverSubmittedOrder(pipelineContext.rbiOrderId);

      if (recovery.status === 'submitted') {
        this.logger.log('Commit mutation failed but order was already submitted', {
          rbiOrderId: pipelineContext.rbiOrderId,
          orderStatus: recovery.order.status,
        });
        await this.fireCommitAnalytics(pipelineContext, orderForAnalytics);
        usePaymentFlowStore.getState().setCurrentState('insertRequested');
        return this.success();
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown commit order error';

      return this.error(
        'COMMIT_ORDER_FAILED',
        errorMessage,
        'Unable to complete your order. Please try again. Error code: (Order Failed)',
        'CommitOrderStep' // retryFromStep - allow retry of the commit
      );
    }
  }


  private watchOfflineDuringCommit(timeoutMs: number): {
    stop: () => void;
    offlineTimeout: Promise<void>;
  } {
    let stopped = false;
    let offlineTimer: ReturnType<typeof setTimeout> | null = null;
    let resolveOfflineTimeout: (() => void) | null = null;

    const offlineTimeout = new Promise<void>(resolve => {
      resolveOfflineTimeout = resolve;
    });

    const clearOfflineTimer = () => {
      if (offlineTimer) {
        clearTimeout(offlineTimer);
        offlineTimer = null;
      }
    };

    const markConfirming = () => {
      usePaymentFlowStore.getState().enterOfflineConfirming();
    };

    const onDisconnected = () => {
      markConfirming();
      if (stopped || offlineTimer || !resolveOfflineTimeout) {
        return;
      }
      offlineTimer = setTimeout(() => {
        resolveOfflineTimeout?.();
      }, timeoutMs);
    };

    const onConnected = () => {
      clearOfflineTimer();
    };

    void Network.getStatus()
      .then(status => {
        if (!status.connected) {
          onDisconnected();
        }
      })
      .catch(() => {
      });

    let lastConnected: boolean | null = null;
    const listener = Network.addListener(status => {
      if (lastConnected === null) {
        lastConnected = status.connected;
        return;
      }
      if (lastConnected === status.connected) {
        return;
      }
      lastConnected = status.connected;

      if (!status.connected) {
        onDisconnected();
      } else {
        onConnected();
      }
    });

    return {
      offlineTimeout,
      stop: () => {
        stopped = true;
        clearOfflineTimer();
        listener.remove();
      },
    };
  }

  private async fireCommitAnalytics(
    pipelineContext: PipelineStepContext,
    orderForAnalytics: CommittedOrder | null
  ): Promise<void> {
    if (!pipelineContext.onCommitSuccess) {
      return;
    }

    try {
      const networkStatus = await Network.getStatus();
      if (!networkStatus.connected) {
        this.logger.log('Purchase analytics skipped: offline after commit', {
          rbiOrderId: pipelineContext.rbiOrderId,
        });
        return;
      }
    } catch {
      this.logger.log('Purchase analytics skipped: network status unavailable', {
        rbiOrderId: pipelineContext.rbiOrderId,
      });
      return;
    }

    if (orderForAnalytics) {
      try {
        await pipelineContext.onCommitSuccess(orderForAnalytics);
      } catch (error) {
        this.logger.logError('Failed to fire purchase analytics after commit', error);
      }
      return;
    }

    this.logger.logError('Purchase analytics skipped: pricedOrder not available in context', {
      rbiOrderId: pipelineContext.rbiOrderId,
      creditType: pipelineContext.creditType,
      processor: pipelineContext.processor,
      paymentMethod: pipelineContext.paymentMethod,
      commitSucceeded: true,
    });
  }

  private buildCommitOrderPayload(
    pipelineContext: PipelineStepContext,
    vaultResult?: VaultResult | null
  ): IMutationCommitOrderArgs {
    // Recompute fireOrderIn against the current clock so it stays in sync with
    // the x-user-datetime header attached by the HTTP link. Without this, a
    // slot picked on an offer's edge can drift past the offer end time by
    // (now - slotSelectionTime) and trigger LOYALTY_TRANSACTION_VALIDATION_ERROR.
    // See FHSG-433.
    let fireOrderIn = pipelineContext.getFireOrderIn?.() ?? pipelineContext.fireOrderIn ?? 0;

    const isDriveThrough = pipelineContext.serviceMode === ServiceMode.DRIVE_THRU;
    if (isDriveThrough && fireOrderIn === 0) {
      fireOrderIn = 1;
    }

    const input: ICommitOrderInput = {
      rbiOrderId: pipelineContext.rbiOrderId,
      fireOrderIn,
      creditType: pipelineContext.creditType as unknown as CartPaymentCardType,
    };

    // Build payment details based on payment method
    switch (pipelineContext.paymentMethod) {
      case 'newCard':
        this.buildNewCardPayment(
          input,
          pipelineContext as NewCardContext,
          vaultResult ?? undefined
        );
        break;
      case 'savedCard':
        this.buildSavedCardPayment(input, pipelineContext as SavedCardContext);
        break;
      case 'applePay':
        this.buildApplePayPayment(input, pipelineContext as ApplePayContext);
        break;
      case 'googlePay':
        this.buildGooglePayPayment(input, pipelineContext as GooglePayContext);
        break;
      case 'giftCard':
        this.buildGiftCardPayment(input, pipelineContext as GiftCardContext);
        break;
    }

    // Build delivery input if delivery order
    const delivery: ICommitDeliveryInput | null =
      pipelineContext.delivery && pipelineContext.serviceMode === ServiceMode.DELIVERY
        ? {
          dropoff: pipelineContext.delivery.dropoff,
          instructions: pipelineContext.delivery.instructions,
          pushNotification: pipelineContext.delivery.pushNotification,
          tipCents: pipelineContext.delivery.tipCents,
        }
        : null;

    return {
      // type errors with deprecated values for apple pay
      input: input as ICommitOrderInputGenerated,
      delivery,
      skipCoolingPeriod: pipelineContext.skipCoolingPeriod ?? false,
    };
  }

  private buildNewCardPayment(
    input: ICommitOrderInput,
    pipelineContext: NewCardContext,
    vaultResult?: VaultResult
  ): void {
    // When we vaulted first (save-card flow), use the vaulted account for payment; no encryption needed for commit.
    if (vaultResult?.success && vaultResult.accountIdentifier) {
      const payment: IPaymentInput = {
        fullName: pipelineContext.fullName,
      };
      if (pipelineContext.processor === PaymentProcessor.WORLDPAY) {
        payment.worldpayInput = {
          storedPaymentMethodId: vaultResult.accountIdentifier,
          storePaymentMethod: false,
        };
      } else if (pipelineContext.processor === PaymentProcessor.ORBITAL) {
        payment.orbitalInput = {
          accountIdentifier: vaultResult.accountIdentifier,
          savePaymentMethod: false,
        };
      }
      input.payment = payment;
      return;
    }

    if (!pipelineContext.encryptionResult) {
      throw new Error('RETRY_FROM_ENCRYPTION');
    }

    const payment: IPaymentInput = {
      fullName: pipelineContext.fullName,
    };

    if (pipelineContext.processor === PaymentProcessor.ORBITAL) {
      this.buildOrbitalNewCardPayment(payment, pipelineContext);
    } else if (pipelineContext.processor === PaymentProcessor.WORLDPAY) {
      this.buildWorldpayNewCardPayment(payment, pipelineContext);
    }

    input.payment = payment;
  }

  private buildOrbitalNewCardPayment(
    payment: IPaymentInput,
    pipelineContext: NewCardContext
  ): void {
    const encResult = pipelineContext.encryptionResult as OrbitalEncryptionResult;

    // Add billing address (postalCode is all we have for new card)
    payment.billingAddress = {
      postalCode: pipelineContext.cardData.postalCode,
    };

    // Add ccMetadata (required by backend for fraud prevention)
    // For Orbital, last4 comes from cardData (not in encryptionResult)
    // Get BIN - use encryption result if available, otherwise get from card data
    const last4 = pipelineContext.cardData.cardNumber.slice(-4);
    const bin =
      encResult.bin && encResult.bin.length >= 6
        ? encResult.bin.substring(0, 6)
        : pipelineContext.cardData.cardNumber.substring(0, 6);

    payment.ccMetadata = {
      ccBin: bin,
      ccExpiryMonth: pipelineContext.cardData.expiryMonth,
      ccExpiryYear: pipelineContext.cardData.expiryYear,
      ccLast4: last4,
    };

    payment.orbitalInput = {
      encryptedPayload: {
        bin: encResult.bin,
        cardBrand: encResult.cardBrand.toLowerCase(),
        encryptedCardNum: encResult.encryptedCardNum,
        encryptedCvv: encResult.encryptedCvv,
        expiryMonth: pipelineContext.cardData.expiryMonth,
        expiryYear: pipelineContext.cardData.expiryYear,
        pieFormat: encResult.pieFormat,
        pieIntegrityCheck: encResult.pieIntegrityCheck,
        pieKeyID: encResult.pieKeyID,
        pieMode: encResult.pieMode,
        piePhaseId: encResult.piePhaseId.toString(),
        pieSubscriberId: encResult.pieSubscriberId,
      },
      // Don't ask backend to vault - explicit vaulting happens before commit in CommitOrderStep
      savePaymentMethod: false,
    };
  }

  private buildWorldpayNewCardPayment(
    payment: IPaymentInput,
    pipelineContext: NewCardContext
  ): void {
    const encResult = pipelineContext.encryptionResult as WorldpayEncryptionResult;

    // Add billing address (postalCode is all we have for new card)
    payment.billingAddress = {
      postalCode: pipelineContext.cardData.postalCode,
    };

    // Add ccMetadata (required by backend for fraud prevention)
    // Get BIN from card data if encryption result doesn't have it (Worldpay sometimes returns empty bin)
    const bin =
      encResult.bin && encResult.bin.length >= 6
        ? encResult.bin.substring(0, 6)
        : pipelineContext.cardData.cardNumber.substring(0, 6);

    payment.ccMetadata = {
      ccBin: bin,
      ccExpiryMonth: pipelineContext.cardData.expiryMonth,
      ccExpiryYear: pipelineContext.cardData.expiryYear,
      ccLast4: encResult.last4,
    };

    payment.worldpayInput = {
      lowValueToken: encResult.lowValueToken,
      // Don't ask backend to vault - explicit vaulting happens before commit in CommitOrderStep
      storePaymentMethod: false,
    };
  }

  private buildSavedCardPayment(input: ICommitOrderInput, pipelineContext: SavedCardContext): void {
    const payment: IPaymentInput = {
      fullName: pipelineContext.fullName || '', // May not always be available for saved cards
    };

    if (pipelineContext.processor === PaymentProcessor.ORBITAL) {
      payment.orbitalInput = {
        accountIdentifier: pipelineContext.savedCardId,
        savePaymentMethod: false,
      };
    } else if (pipelineContext.processor === PaymentProcessor.WORLDPAY) {
      payment.worldpayInput = {
        storedPaymentMethodId: pipelineContext.savedCardId,
        storePaymentMethod: false,
      };
    }

    input.payment = payment;
  }

  private buildApplePayPayment(input: ICommitOrderInput, pipelineContext: ApplePayContext): void {
    const { applePayDetails, processor } = pipelineContext;

    // Backend requires country to be CountryCode (e.g. 'US' | 'CA'). Ensure it is set.
    const country = applePayDetails.country === 'CA' ? 'CA' : ('US' as const);
    const normalizedApplePayDetails = { ...applePayDetails, country };

    this.logger.log('[ApplePay] Building payment payload', {
      processor,
      hasBillingAddress: !!normalizedApplePayDetails.billingAddress,
      billingAddress: normalizedApplePayDetails.billingAddress,
      postalCodeLength: normalizedApplePayDetails.billingAddress?.postalCode?.length,
      country: normalizedApplePayDetails.country,
    });

    // For Worldpay, match workspace/frontend structure:
    // - applePayDetails goes INSIDE payment object (not at input level)
    // - fullName is empty string in payment (match frontend behavior)
    if (processor === PaymentProcessor.WORLDPAY) {
      const payment: IPaymentInput = {
        fullName: '', // Match frontend: empty string, not actual fullName
        applePayDetails: normalizedApplePayDetails, // Inside payment object
      };

      payment.worldpayInput = {
        lowValueToken: applePayDetails.paymentData, // Worldpay low value token from conversion step
        storePaymentMethod: false,
      };

      this.logger.log('[ApplePay] Worldpay payment structure', {
        hasApplePayDetailsInPayment: !!payment.applePayDetails,
        hasWorldpayInput: !!payment.worldpayInput,
        lowValueTokenLength: payment.worldpayInput.lowValueToken?.length,
        fullNameInPayment: payment.fullName,
      });

      input.payment = payment;
    } else {
      // Orbital: payment { fullName, applePayDetails } only (match workspace/frontend commitOrder({ payment: { ...digitalPayDetails } }))
      input.payment = {
        fullName: '',
        applePayDetails: normalizedApplePayDetails,
      };
    }
  }

  private buildGooglePayPayment(input: ICommitOrderInput, pipelineContext: GooglePayContext): void {
    const { googlePayDetails, fullName, processor } = pipelineContext;

    if (processor === PaymentProcessor.WORLDPAY) {
      const payment: IPaymentInput = {
        fullName: fullName || '',
        googlePayDetails, // Inside payment, same as Apple Pay has applePayDetails in payment
      };

      payment.worldpayInput = {
        lowValueToken: googlePayDetails.paymentData,
        storePaymentMethod: false,
      };

      input.payment = payment;
    } else {
      // Orbital: payment { fullName, googlePayDetails } only  
      input.payment = {
        fullName: fullName || '',
        googlePayDetails,
      };
    }
  }

  private buildGiftCardPayment(input: ICommitOrderInput, pipelineContext: GiftCardContext): void {
    // For gift card (prepaid) payments:
    // - Set creditType to GIFT_CARD
    // - Use payment object with storedPaymentMethodId (same as saved cards)
    // Note: Gift cards are currently only supported with Worldpay
    input.creditType = CartPaymentCardType.GIFT_CARD;

    input.payment = {
      fullName: pipelineContext.fullName || '',
      worldpayInput: {
        storedPaymentMethodId: pipelineContext.giftCardAccountId,
        storePaymentMethod: false,
      },
    };
  }

  private shouldVaultCard(pipelineContext: PipelineStepContext): boolean {
    if (pipelineContext.paymentMethod !== 'newCard') {
      return false;
    }

    const newCardContext = pipelineContext as NewCardContext;

    // Only vault if:
    // 1. User wants to save the card
    // 2. Encryption was successful
    // 3. Vault service is available
    return newCardContext.cardData.vaultCard && !!newCardContext.encryptionResult;
  }

  private async attemptVaulting(pipelineContext: NewCardContext): Promise<VaultResult> {
    if (!pipelineContext.encryptionResult) {
      this.logger.log('No encryption result - skipping vault');
      return {
        success: false,
        error: 'No encryption result available',
      };
    }

    try {
      usePaymentFlowStore.getState().setCurrentState('vaulting');

      this.logger.log('Attempting to vault card after order commit', {
        processor: pipelineContext.processor,
      });

      const vaultService = new VaultService({
        storeId: pipelineContext.storeId,
        cardData: pipelineContext.cardData,
        logger: this.logger,
      });

      const result = await this.vaultWithTimeout(
        vaultService.vaultCard(pipelineContext.encryptionResult, pipelineContext.processor)
      );

      this.logger.log('Card vaulting successful', {
        processor: pipelineContext.processor,
        accountIdentifier: result.accountIdentifier,
      });

      return result;
    } catch (error) {
      this.logger.logError('Card vaulting failed - order still succeeded', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown vaulting error',
        // Order completed successfully, user just won't have a card saved
      };
    }
  }

  /**
   * Wraps the vault promise with a timeout so a hanging mutation/refetch
   * does not leave the UI stuck on "Processing Order..." forever.
   */
  private vaultWithTimeout(vaultPromise: Promise<VaultResult>): Promise<VaultResult> {
    const VAULT_TIMEOUT_MS = 30000;

    const timeoutPromise = new Promise<never>((_, reject) => {
      const id = setTimeout(() => {
        clearTimeout(id);
        reject(new Error('Vault request timed out'));
      }, VAULT_TIMEOUT_MS);
    });

    return Promise.race([vaultPromise, timeoutPromise]);
  }
}
